import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Heart, Lock, TrendingUp, Users, CreditCard, FileText, Euro, MessageSquare } from "lucide-react";

const TOKEN_KEY = "rawlove_admin_token";

function Metric({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E8DDD5] p-6 shadow-[0_4px_20px_-2px_rgba(110,45,56,0.06)]">
      <div className="flex items-center gap-2 text-[#8C6B64] mb-3">
        <Icon className="w-4 h-4" style={{ color: accent }} />
        <span className="text-xs font-semibold tracking-wide uppercase">{label}</span>
      </div>
      <p className="font-editorial text-4xl font-semibold" style={{ color: accent }}>{value}</p>
    </div>
  );
}

function FunnelRow({ label, value, max }) {
  const pct = max ? Math.round((value / max) * 100) : 0;
  return (
    <div className="mb-3">
      <div className="flex justify-between text-sm mb-1">
        <span className="text-[#4A3B37]">{label}</span>
        <span className="font-semibold text-[#2B1E1B]">{value}</span>
      </div>
      <div className="h-2.5 rounded-full bg-[#F2E9E1] overflow-hidden">
        <div className="h-full bg-[#6E2D38] rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [input, setInput] = useState("");
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (t) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/stats", { headers: { "X-Admin-Token": t } });
      setStats(data);
      setToken(t);
      localStorage.setItem(TOKEN_KEY, t);
    } catch (e) {
      setError("Token invalide.");
      setStats(null);
      localStorage.removeItem(TOKEN_KEY);
      setToken("");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) load(token); /* eslint-disable-next-line */ }, []);

  if (!stats) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-[#FAF7F2] px-5">
        <div className="bg-white rounded-2xl border border-[#E8DDD5] p-8 w-full max-w-sm shadow-lg" data-testid="admin-login">
          <div className="w-12 h-12 rounded-full bg-[#F4E3E2] flex items-center justify-center mx-auto mb-5">
            <Lock className="w-6 h-6 text-[#6E2D38]" />
          </div>
          <h1 className="font-editorial text-2xl font-semibold text-center mb-1">Espace administration</h1>
          <p className="text-sm text-[#8C6B64] text-center mb-6">RawLove Analytics</p>
          <input type="password" value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(input)}
            placeholder="Token d'accès" data-testid="admin-token-input"
            className="w-full rounded-xl border border-[#E8DDD5] px-4 py-3 outline-none focus:border-[#C48B92] mb-3" />
          {error && <p className="text-sm text-[#A83A3A] mb-3">{error}</p>}
          <button onClick={() => load(input)} disabled={loading} data-testid="admin-login-button"
            className="w-full bg-[#6E2D38] hover:bg-[#54212A] text-white rounded-full py-3 font-medium transition-all">
            {loading ? "Connexion…" : "Accéder"}
          </button>
        </div>
      </div>
    );
  }

  const s = stats;
  const max = Math.max(s.landing_view, s.analysis_started, 1);

  return (
    <div className="min-h-[100dvh] bg-[#FAF7F2]">
      <header className="backdrop-blur-xl bg-[#FFFDF9]/90 border-b border-[#E8DDD5]">
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-[#6E2D38] fill-[#6E2D38]" />
            <span className="font-editorial text-2xl font-semibold">RawLove · Admin</span>
          </div>
          <button onClick={() => { localStorage.removeItem(TOKEN_KEY); setStats(null); setToken(""); }}
            className="text-sm text-[#8C6B64] hover:text-[#6E2D38]">Déconnexion</button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-5 py-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Metric icon={Euro} label="Revenus" value={`${s.revenue_eur.toFixed(2)} €`} accent="#6E2D38" />
          <Metric icon={CreditCard} label="Paiements" value={s.payments_success} accent="#2E6B4F" />
          <Metric icon={FileText} label="Analyses générées" value={s.analyses_generated} accent="#C48B92" />
          <Metric icon={TrendingUp} label="Conversion" value={`${s.conversion_rate}%`} accent="#6E2D38" />
        </div>

        <div className="grid lg:grid-cols-2 gap-5">
          <div className="bg-white rounded-2xl border border-[#E8DDD5] p-6 shadow-[0_4px_20px_-2px_rgba(110,45,56,0.06)]">
            <h2 className="font-editorial text-2xl font-semibold mb-5 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#6E2D38]" /> Entonnoir de conversion
            </h2>
            <FunnelRow label="Visites (landing)" value={s.landing_view} max={max} />
            <FunnelRow label="Analyses démarrées" value={s.analysis_started} max={max} />
            <FunnelRow label="1er message envoyé" value={s.first_message_sent} max={max} />
            <FunnelRow label="Conversations terminées" value={s.conversation_completed} max={max} />
            <FunnelRow label="Passages au paiement" value={s.checkout_started} max={max} />
            <FunnelRow label="Paiements réussis" value={s.payments_success} max={max} />
          </div>

          <div className="bg-white rounded-2xl border border-[#E8DDD5] p-6 shadow-[0_4px_20px_-2px_rgba(110,45,56,0.06)]">
            <h2 className="font-editorial text-2xl font-semibold mb-5 flex items-center gap-2">
              <Users className="w-5 h-5 text-[#6E2D38]" /> Activité
            </h2>
            <div className="space-y-3">
              {[
                [MessageSquare, "Conversations totales", s.conversations_total],
                [MessageSquare, "Analyses démarrées", s.analysis_started],
                [MessageSquare, "Conversations terminées", s.conversation_completed],
                [CreditCard, "Passages au paiement", s.checkout_started],
                [CreditCard, "Paiements réussis", s.payments_success],
                [FileText, "Analyses générées", s.analyses_generated],
              ].map(([Icon, label, val]) => (
                <div key={label} className="flex items-center justify-between py-2 border-b border-[#F2E9E1] last:border-0">
                  <span className="flex items-center gap-2 text-[#4A3B37]">
                    <Icon className="w-4 h-4 text-[#C48B92]" /> {label}
                  </span>
                  <span className="font-semibold">{val}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
